<script setup lang="ts">
import { captureSentryException, addSentryBreadcrumb } from '@/config/sentry'

// Test functions for Sentry integration
const testJavaScriptError = () => {
  addSentryBreadcrumb('User clicked test JavaScript error button', 'user-action')
  throw new Error('Test JavaScript Error - This is a test error for Sentry integration')
}

const testVueError = () => {
  addSentryBreadcrumb('User clicked test Vue error button', 'user-action')
  // Simulate a Vue-specific error
  const error = new Error('Test Vue Error - This is a test Vue component error')
  captureSentryException(error, {
    component: 'SentryTestButtons',
    action: 'testVueError',
    userAgent: navigator.userAgent
  })
}

const testAsyncError = async () => {
  addSentryBreadcrumb('User clicked test async error button', 'user-action')
  try {
    // Simulate async operation that fails
    await new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error('Test Async Error - This is a test async error'))
      }, 1000)
    })
  } catch (error) {
    captureSentryException(error as Error, {
      component: 'SentryTestButtons',
      action: 'testAsyncError',
      errorType: 'async'
    })
  }
}

const testNetworkError = async () => {
  addSentryBreadcrumb('User clicked test network error button', 'user-action')
  try {
    // Simulate network request that fails
    const response = await fetch('https://httpstat.us/500')
    if (!response.ok) {
      throw new Error(`Network Error: ${response.status} ${response.statusText}`)
    }
  } catch (error) {
    captureSentryException(error as Error, {
      component: 'SentryTestButtons',
      action: 'testNetworkError',
      errorType: 'network'
    })
  }
}

const testCustomMessage = () => {
  addSentryBreadcrumb('User clicked test custom message button', 'user-action')
  
  if (typeof window !== 'undefined' && window.Sentry) {
    window.Sentry.captureMessage('Test Custom Message - This is a test message for Sentry', 'info')
  }
}

const testUserFeedback = () => {
  addSentryBreadcrumb('User clicked test user feedback button', 'user-action')
  
  if (typeof window !== 'undefined' && window.Sentry) {
    window.Sentry.showReportDialog({
      title: 'Test User Feedback',
      subtitle: 'This is a test of the Sentry user feedback dialog.',
      subtitle2: 'You can close this dialog or submit test feedback.',
    })
  }
}
</script>

<template>
  <!-- Only show in development mode -->
  <div v-if="!$route.meta?.production" class="fixed bottom-4 right-4 z-50">
    <details class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 shadow-lg">
      <summary class="cursor-pointer text-sm font-semibold text-yellow-800 mb-2">
        🧪 Sentry Test Controls
      </summary>
      
      <div class="space-y-2">
        <p class="text-xs text-yellow-700 mb-3">
          These buttons test different Sentry error reporting scenarios.
          <br><strong>Only visible in development mode.</strong>
        </p>
        
        <div class="grid grid-cols-2 gap-2">
          <button
            @click="testJavaScriptError"
            class="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
            title="Throws a JavaScript error"
          >
            JS Error
          </button>
          
          <button
            @click="testVueError"
            class="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            title="Simulates a Vue component error"
          >
            Vue Error
          </button>
          
          <button
            @click="testAsyncError"
            class="px-3 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
            title="Simulates an async operation error"
          >
            Async Error
          </button>
          
          <button
            @click="testNetworkError"
            class="px-3 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
            title="Simulates a network request error"
          >
            Network Error
          </button>
          
          <button
            @click="testCustomMessage"
            class="px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
            title="Sends a custom info message"
          >
            Custom Message
          </button>
          
          <button
            @click="testUserFeedback"
            class="px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
            title="Opens user feedback dialog"
          >
            User Feedback
          </button>
        </div>
        
        <p class="text-xs text-yellow-600 mt-2">
          Check your Sentry dashboard to see captured errors and messages.
        </p>
      </div>
    </details>
  </div>
</template>

<style scoped>
/* Component-specific styles */
</style>